import React, { FunctionComponent, Suspense, useEffect, useMemo } from 'react'
import { FormattedMessage, IntlProvider } from 'react-intl'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { Provider, useSelector } from 'react-redux'
import { createRoutesFromElements, Navigate, Outlet, Route, RouterProvider, useLocation } from 'react-router'

import { useLDClient } from 'launchdarkly-react-client-sdk'
import { isEmpty } from 'lodash-es'
import { PersistGate } from 'redux-persist/integration/react'
import styled from 'styled-components'
import { useTheme } from 'styled-components/native'
import { FocusVisibleManager } from 'use-focus-visible'

// Store must be imported first or else test will not load app for login via Cypress
// oxlint-disable-next-line import-eslint/order
import store, { persistor } from './data/store'

import { usePrevious } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/usePrevious'
import { withLDProvider } from '@lyrahealth-inc/shared-app-logic/src/features/flags/launchdarkly-client-sdk.web'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  LAUNCHDARKLY_WEB_CLIENT_ID,
  LD_SHARED_CONTEXT_KEYS,
  shouldDisableCostlyVendors,
} from '@lyrahealth-inc/shared-app-logic/src/features/flags/utils'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import LDOverride from '@lyrahealth-inc/ui-core-crossplatform/src/ldOverride/LDOverride'
import { colors } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/colors'
import { HostWrapper } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/hostWrapper/HostWrapper'
import { ThemeProvider } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { ThemeScope } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeScope'

import AdminHome from './admin/AdminHome'
import AdminRoutes from './admin/AdminRoutes'
import NpProgramAdmin from './admin/npProgram/NpProgramAdmin'
import AppContainer from './AppContainer'
import { CalendarActivationNavBar } from './calendar/CalendarActivationNavBar'
import CalendarCallback from './calendar/CalendarCallback'
import CalendarSetup from './calendar/CalendarSetup'
import CalendarSetupComplete from './calendar/CalendarSetupComplete'
import CurrentCalendar from './calendar/CurrentCalendar'
import ClientsDashboard from './clients/dashboard/ClientsDashboard'
// Root Pages
import OutcomesConsent from './clients/dashboard/outcomesConsent/OutcomesConsent'
import ClientDetails from './clients/individualClient/ClientDetails'
import AlertsContainer from './common/components/alerts/AlertsContainer'
import SelectCalendars from './common/components/calendar/SelectCalendars'
import SessionLookup from './common/components/clientSession/SessionLookup'
import { DevMocksProvider } from './common/components/devTools/DevMocksContext'
import DownloadPaymentHistory from './common/components/downloadPaymentHistory/downloadPaymentHistory'
import GoSearchBar from './common/components/goSearchBar/GoSearchBar'
import GoSearch from './common/components/goSearchModal/GoSearch'
import Header from './common/components/layout/header/Header'
import PageLoader from './common/components/pageLoader/PageLoader'
import ProviderSession from './common/components/providerSession/ProviderSession'
import AuthorizedRoute from './common/components/route/authorizedRoute/AuthorizedRoute'
import NotFoundRoute from './common/components/route/notFoundRoute/NotFoundRoute'
import PublicRoute from './common/components/route/publicRoute/PublicRoute'
import ToastContainer from './common/components/ToastContainer/ToastContainer'
import { APPOINTMENT_OPERATIONS, BEND_ROLES, ROLES, SUPERVISOR_ROLES } from './common/constants/appConstants'
import {
  ADMIN,
  ADMIN_NP_PROGRAM,
  BC_CASELOAD,
  BC_CASELOAD_DETAILED_WEEKLY_VIEW,
  BC_MEASUREMENT_BASED_CARE,
  BC_PERFORMACE_DETAILED_WEEKLY_VIEW,
  BC_PERFORMANCE,
  BC_PLATFORM_ENGAGEMENT,
  BC_PLATFORM_ENGAGEMENT_QUARTERLY_DETAILS,
  CALENDAR,
  CARE_INSIGHTS,
  CLIENT_ACTIVITIES,
  CLIENT_DOCUMENTS,
  CLIENT_EMAIL,
  CLIENT_END_OF_SESSION,
  CLIENT_EVALUATION,
  CLIENT_EVALUATION_ADD_MEASURES,
  CLIENT_HOME,
  CLIENT_MEDICATIONS,
  CLIENT_NEW_NOTE,
  CLIENT_NEW_SAFETY_PLAN,
  CLIENT_NOTE,
  CLIENT_NOTES,
  CLIENT_OUTCOMES,
  CLIENT_PROGRESS,
  CLIENT_RECORDS,
  CLIENT_RISK_SUMMARY,
  CLIENT_SAFETY_PLAN,
  CLIENT_SESSION_LOOKUP,
  CLIENTS,
  CLIENTS_ASSIGNMENT_DETAILS,
  CLIENTS_CANCEL_SESSION,
  CLIENTS_CONTENT_LIBRARY,
  CLIENTS_DETAILS,
  CLIENTS_NEW_ASSIGNMENT,
  CLIENTS_NEW_SESSION,
  CLIENTS_NEW_SESSION_CONFIRMATION,
  CLIENTS_NEW_SESSION_DETAILS,
  CLIENTS_NEW_TRACK,
  CLIENTS_RESCHEDULE_SESSION,
  CLIENTS_RESCHEDULE_SESSION_CONFIRMATION,
  CLIENTS_RESCHEDULE_SESSION_DETAILS,
  CLIENTS_SESSIONS,
  CLIENTS_TRACK_LIBRARY,
  CONTENT_BUILDER,
  CONTENT_BUILDER_ACTIVITY_RENDER,
  DASHBOARD,
  DOCUMENTS,
  DOWNLOADS,
  ELIGIBILITY_CHECKER,
  HEALTHPLAN_INFO,
  LC_CALENDAR,
  LC_CALENDAR_ACTIVATE,
  LC_CALENDAR_ACTIVATE_SETUP,
  LC_CALENDAR_CALLBACK,
  LC_CALENDAR_CURRENT,
  LC_CALENDAR_EDIT,
  LC_CALENDAR_EDIT_SELECT_CALENDARS,
  LC_CALENDAR_SELECT_CALENDARS,
  LC_CALENDAR_SETUP,
  LC_CALENDAR_SETUP_COMPLETE,
  LOGIN,
  LT_CLIENTS,
  MESSENGER,
  MESSENGER_POPOUT,
  MY_INFO,
  OKTACALLBACK,
  OUTCOMES_CONSENT,
  PAYMENT_HISTORY,
  PAYMENTS,
  PRACTICES,
  PRACTICES_REGISTER,
  PROVIDERS,
  PROVIDERS_REGISTER,
  RECONCILER,
  RECONCILER_DETAILS,
  REQUEST_PAYMENT,
  SCHEDULING,
  SCHEDULING_ACTIVATE,
  SCHEDULING_OAUTH_CALLBACK,
  SETTINGS,
} from './common/constants/routingConstants'
import { PAGE_SCROLL_CONTAINER_ID } from './common/utils/scrollToPageTop'
import { hasRole, shouldHideMainNav } from './common/utils/utils'
import ApplicationContext from './contextProviders/ApplicationContext'
import { PreviousRouteProvider } from './contextProviders/PreviousRouteContextProvider'
import DashboardContainer from './dashboard/DashboardContainer'
import { getAuthConfig, getAuthLTProviders, getAuthSupervisor } from './data/auth/authSelectors'
import ZendeskWidget from './data/ZendeskWidget'
import EligibilityChecker from './eligibilityChecker/EligibilityChecker'
import { sentryCreateBrowserRouter } from './index'
import Login from './login/Login'
import OktaCallback from './login/OktaCallback'
import AssignmentDetails from './lyraTherapy/assignments/assignmentDetails/AssignmentDetails'
import Assignments from './lyraTherapy/assignments/Assignments'
import CareTimelineRoute from './lyraTherapy/careTimeline/CareTimelineRoute'
import ClientDocuments from './lyraTherapy/clientDocuments/ClientDocuments'
import ClientNoteDetails from './lyraTherapy/clientNotes/ClientNoteDetails'
import ClientNotes from './lyraTherapy/clientNotes/ClientNotes'
import ClientNotesRoutes from './lyraTherapy/clientNotes/ClientNotesRoutes'
import ClientProgress from './lyraTherapy/clientProgress/ClientProgress'
import EndOfSession from './lyraTherapy/clients/clientDetails/EndOfSession'
import BenefitsSummaryContainer from './lyraTherapy/clients/clientDetails/redesigned/BenefitsSummaryContainer'
import Clients from './lyraTherapy/clients/clientsContainer/Clients'
import ClientsContainer from './lyraTherapy/clients/clientsContainer/ClientsContainer'
import ClientShareOutcomes from './lyraTherapy/clientShareOutcomes/ClientShareOutcomes'
import ConfigAssignmentContainer from './lyraTherapy/configAssignmentContainer/ConfigAssignmentContainer'
import ConfigTrackContainer from './lyraTherapy/configTrackContainer/ConfigTrackContainer'
import ContentLibrary from './lyraTherapy/contentLibrary/ContentLibrary'
import TrackLibrary from './lyraTherapy/contentLibrary/TrackLibrary'
import DocumentsPage from './lyraTherapy/documents/DocumentsPage'
import Messenger from './lyraTherapy/messages/messenger/Messenger'
import PopoutMessenger from './lyraTherapy/messages/messenger/PopoutMessenger'
import CareInsights from './lyraTherapy/performance/CareInsights/CareInsights'
import CaseloadManagementContainer from './lyraTherapy/performance/CaseloadManagementContainer'
import CaseloadManagementDetailedWeeklyViewContainer from './lyraTherapy/performance/CaseloadManagementDetailedWeeklyViewContainer'
import PlatformEngagementContainer from './lyraTherapy/performance/platformEngagement/PlatformEngagementContainer'
import PlatformEngagementQuarterlyDetailsContainer from './lyraTherapy/performance/platformEngagement/PlatformEngagementQuarterlyDetailsContainer'
import ProviderCaseload from './lyraTherapy/performance/ProviderCaseload'
import ScheduleSessionFullScreenLayout from './lyraTherapy/sessions/ScheduleSessionFullScreenLayout'
import SessionDetailsContainer from './lyraTherapy/sessions/SessionDetailsContainer'
import SessionSelectionConfirmation from './lyraTherapy/sessions/SessionSelectionConfirmation'
import SessionSelectionContainer from './lyraTherapy/sessions/SessionSelectionContainer'
import PaymentHistory from './paymentHistory/PaymentHistory'
import ReconcilerDashboard from './reconciler/dashboard/ReconcilerDashboard'
import ReconcilerDetails from './reconciler/individualClient/ReconcilerDetails'
import ClientEmail from './requestPayment/clientEmail/ClientEmail'
import CalendarRoutes from './routing/CalendarRoutes'
import ClientsRoutes from './routing/ClientsRoutes'
import HealthPlanInfoRoutes from './routing/HealthPlanInfoRoutes'
import MyInfoRoutes from './routing/MyInfoRoutes'
import { UNSAFE_listenBeforeNavigate, UNSAFE_patchRouterNavigate } from './routing/patchRouterNavigate'
import PaymentsRoutes from './routing/PaymentsRoutes'
import PracticesRoutes from './routing/PracticesRoutes'
import preserveSearchParamsListener from './routing/preserveSearchParams'
import ProvidersRoutes from './routing/ProvidersRoutes'
import ReconcilerRoutes from './routing/ReconcilerRoutes'
import RequestPaymentRoutes from './routing/RequestPaymentRoutes'
import usePathChanged from './routing/usePathChanged'
import SchedulingOAuth from './scheduling/SchedulingOAuth'
import SmartScheduling from './scheduling/SmartScheduling'
import SchedulingWalkthrough from './scheduling/walkthrough/SchedulingWalkthrough'
import Settings from './settings/Settings'
import { initializeTrackingWithProxy } from '../../mixpanel/mixpanelTracking'
import ErrorBoundaryWrapper from '../js/error/ErrorBoundaryWrapper'
import Client from './lyraTherapy/clients/clientDetails/Client'
import ClientRecords from './lyraTherapy/clients/clientDetails/clientRecords/ClientRecords'
import { getLTVideoSessionOpen } from './lyraTherapy/data/ltVideoSelectors'
import Medications from './lyraTherapy/medications/Medications'
import NpTestPlan from './lyraTherapy/npTestPlans/NpTestPlan'
import NpTestPlanMeasureLibrary from './lyraTherapy/npTestPlans/NpTestPlanMeasureLibrary'
import MeasurementBasedCare from './lyraTherapy/performance/MeasurementBasedCare'
import CreateSafetyPlan from './lyraTherapy/safetyPlan/CreateSafetyPlan'
import SafetyPlanPlaceholder from './lyraTherapy/safetyPlan/SafetyPlanPlaceholder'
import SessionCancellation from './lyraTherapy/sessions/SessionCancellation'

const ContentBuilder = __CONTENT_BUILDER_AVAILABLE__
  ? React.lazy(() => import('./contentBuilder/ContentBuilder'))
  : null
const ActivityRendererRoute = __CONTENT_BUILDER_AVAILABLE__
  ? React.lazy(() => import('./contentBuilder/ActivityRendererRoute'))
  : null

// @ts-expect-error TS(7030): Not all code paths return a value.
const getInitialRoute = () => {
  const auth = store.getState().auth
  if (auth.isLoggedIn) {
    const roles = auth.user?.roles
    if (hasRole(roles, [ROLES.PRACTICES_ADMIN, ROLES.INDIVIDUAL_PROVIDER])) {
      if (auth.user?.is_registered) {
        if (auth.config?.bcPlatform) {
          if (hasRole(roles, BEND_ROLES)) {
            return LC_CALENDAR_CURRENT.route
          }

          return DASHBOARD.route
        } else {
          if (hasRole(roles, [ROLES.ICAS_PROVIDER])) {
            return CLIENTS.route
          }
          return REQUEST_PAYMENT.route
        }
      } else if (hasRole(roles, [ROLES.PRACTICES_ADMIN])) {
        return PRACTICES_REGISTER.route
      } else if (hasRole(roles, [ROLES.INDIVIDUAL_PROVIDER])) {
        return PROVIDERS_REGISTER.route
      }
    } else if (hasRole(roles, SUPERVISOR_ROLES)) {
      return LT_CLIENTS.route
    } else if (hasRole(roles, ROLES.PROVIDERS_ADMIN)) {
      return PROVIDERS.route
    } else if (hasRole(roles, ROLES.PAYMENTS_ADMIN)) {
      return PAYMENTS.route
    } else if (hasRole(roles, ROLES.RECONCILER)) {
      return RECONCILER.route
    } else if (hasRole(roles, ROLES.PRACTICE_PROVIDER)) {
      return MY_INFO.route
    } else if (hasRole(roles, ROLES.NP_ADMIN)) {
      return ADMIN.route
    }
  } else {
    return LOGIN.route
  }
}

// Keyed by location: with v7_startTransition, a second navigation to `/` while the first redirect is still
// pending keeps this element mounted, so without a new key its effect would not redirect again.
// A role getInitialRoute doesn't cover gets no route; render nothing, since a keyed redirect to `/` would loop.
const InitialRouteRedirect = () => {
  const { key } = useLocation()
  const route = getInitialRoute()
  return route ? <Navigate key={key} to={route} /> : null
}

const CommonComponentsWrapperContainer = styled.div<{ height?: string; bgColor: string }>(({ height, bgColor }) => ({
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  backgroundColor: bgColor,
  transformStyle: 'preserve-3d',
  height,
}))

const ContentContainerRightOfSideNav = styled.div<{
  marginLeftValue?: string
  isSideNavigationBarEnabled?: boolean
}>(({ marginLeftValue = '102px', isSideNavigationBarEnabled }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  marginLeft: marginLeftValue,
  '--provider-content-left': marginLeftValue,
  overflow: 'auto',
  ...(isSideNavigationBarEnabled && { maxHeight: '100vh' }),
}))

const StickyProviderSessionContainer = styled.div<{ isSticky: boolean; elevateAboveHeader?: boolean }>(
  ({ isSticky, elevateAboveHeader }) => ({
    ...(isSticky && { position: 'sticky', top: 0, zIndex: elevateAboveHeader ? 11 : 6 }),
  }),
)

const CommonComponentsWrapperInner: FunctionComponent = () => {
  const location = useLocation()

  const { colors: themedColors } = useTheme()
  const bgColor = location?.pathname !== '/login' ? themedColors.backgroundPrimary : colors.ui_oatmeal1

  return (
    <CommonComponentsWrapperContainer
      height={
        location.pathname.includes(LC_CALENDAR_CURRENT.route) ||
        location.pathname.includes(MESSENGER.route) ||
        location.pathname.includes(LT_CLIENTS.route) ||
        location.pathname.includes(DASHBOARD.route) ||
        location.pathname.includes(BC_PERFORMANCE.route)
          ? '100vh'
          : undefined
      }
      bgColor={bgColor}
    >
      <CommonComponents />
    </CommonComponentsWrapperContainer>
  )
}

const CommonComponentsWrapper: FunctionComponent = () => (
  <ThemeScope forceNonAurora>
    <CommonComponentsWrapperInner />
  </ThemeScope>
)

const PreviousRoute = () => {
  return (
    <PreviousRouteProvider>
      <Outlet />
    </PreviousRouteProvider>
  )
}

const App = () => {
  initializeTrackingWithProxy()
  const ldClient = useLDClient()

  const router = useMemo(() => {
    const routes = createRoutesFromElements(
      <Route
        element={
          <ErrorBoundaryWrapper>
            <CommonComponentsWrapper />
          </ErrorBoundaryWrapper>
        }
      >
        <Route element={<PreviousRoute />}>
          <Route path='/' Component={InitialRouteRedirect} />
          <Route
            path='/python'
            element={() => (
              <div style={{ textAlign: 'center', paddingTop: '60' }}>
                <LoadingIndicator size={45} />
              </div>
            )}
          />
          <Route element={<PublicRoute />}>
            <Route path={LOGIN.route} element={<Login />} />
            <Route path={OKTACALLBACK.route} element={<OktaCallback />} />
            <Route path={CLIENT_OUTCOMES.route} element={<ClientShareOutcomes />} />
          </Route>
          <Route element={<AuthorizedRoute />}>
            <Route path={DASHBOARD.route} element={<DashboardContainer />} />
            <Route path={`${PRACTICES.route}/*`} element={<PracticesRoutes />} />
            <Route path={`${PROVIDERS.route}/*`} element={<ProvidersRoutes />} />
            <Route path={PAYMENTS.route} element={<PaymentsRoutes />} />
            <Route path={REQUEST_PAYMENT.route} element={<RequestPaymentRoutes />} />
            <Route path={HEALTHPLAN_INFO.route} element={<HealthPlanInfoRoutes />} />
            <Route path={CLIENT_EMAIL.route} element={<ClientEmail />} />
            <Route path={BC_PERFORMANCE.route}>
              <Route index element={<ProviderCaseload />} />
              <Route
                path={BC_PERFORMACE_DETAILED_WEEKLY_VIEW.route}
                element={<CaseloadManagementDetailedWeeklyViewContainer />}
              />
              <Route
                path={BC_CASELOAD_DETAILED_WEEKLY_VIEW.route}
                element={<CaseloadManagementDetailedWeeklyViewContainer />}
              />
              <Route path={BC_CASELOAD.route} element={<CaseloadManagementContainer />} />
              <Route path={BC_MEASUREMENT_BASED_CARE.route} element={<MeasurementBasedCare />} />
              <Route path={BC_PLATFORM_ENGAGEMENT.route} element={<PlatformEngagementContainer />} />
              <Route
                path={BC_PLATFORM_ENGAGEMENT_QUARTERLY_DETAILS.route}
                element={<PlatformEngagementQuarterlyDetailsContainer />}
              />
              <Route path={CARE_INSIGHTS.route} element={<CareInsights />} />
            </Route>
            <Route path={CLIENTS.route} element={<ClientsRoutes />}>
              <Route index element={<ClientsDashboard />} />
              <Route path={CLIENTS_DETAILS.route} element={<ClientDetails />} />
              <Route path={OUTCOMES_CONSENT.route} element={<OutcomesConsent />} />
              <Route path={LT_CLIENTS.route} element={<ClientsContainer />}>
                <Route index element={<Clients />} />
                <Route path={CLIENT_HOME.route} element={<Client />}>
                  <Route index element={<BenefitsSummaryContainer />} />
                  <Route path={CLIENTS_ASSIGNMENT_DETAILS.route} element={<AssignmentDetails />} />
                  <Route path={CLIENTS_NEW_ASSIGNMENT.route} element={<ConfigAssignmentContainer />} />
                  <Route path={CLIENTS_NEW_TRACK.route} element={<ConfigTrackContainer />} />
                  <Route path={CLIENTS_CONTENT_LIBRARY.route} element={<ContentLibrary />} />
                  <Route path={CLIENTS_TRACK_LIBRARY.route} element={<TrackLibrary />} />
                  <Route path={CLIENTS_SESSIONS.route} element={<CareTimelineRoute />} />
                  <Route element={<ScheduleSessionFullScreenLayout />}>
                    <Route
                      path={CLIENTS_NEW_SESSION.route}
                      element={<SessionDetailsContainer operation={APPOINTMENT_OPERATIONS.NEW} />}
                    />
                    <Route
                      path={CLIENTS_NEW_SESSION_DETAILS.route}
                      element={<SessionSelectionContainer operation={APPOINTMENT_OPERATIONS.NEW} />}
                    />
                  </Route>
                  <Route
                    path={CLIENTS_RESCHEDULE_SESSION.route}
                    element={<SessionDetailsContainer operation={APPOINTMENT_OPERATIONS.RESCHEDULE} />}
                  />
                  <Route path={CLIENTS_CANCEL_SESSION.route} element={<SessionCancellation />} />
                  <Route
                    path={CLIENTS_RESCHEDULE_SESSION_DETAILS.route}
                    element={<SessionSelectionContainer operation={APPOINTMENT_OPERATIONS.RESCHEDULE} />}
                  />
                  <Route
                    path={CLIENTS_NEW_SESSION_CONFIRMATION.route}
                    element={<SessionSelectionConfirmation operation={APPOINTMENT_OPERATIONS.NEW} />}
                  />
                  <Route
                    path={`${CLIENTS_NEW_SESSION.route}/confirm/:appointmentId`}
                    element={<SessionSelectionConfirmation operation={APPOINTMENT_OPERATIONS.NEW} />}
                  />
                  <Route
                    path={CLIENTS_RESCHEDULE_SESSION_CONFIRMATION.route}
                    element={<SessionSelectionConfirmation operation={APPOINTMENT_OPERATIONS.RESCHEDULE} />}
                  />
                  <Route
                    path={`${CLIENTS_RESCHEDULE_SESSION.route}/confirm/:appointmentId`}
                    element={<SessionSelectionConfirmation operation={APPOINTMENT_OPERATIONS.RESCHEDULE} />}
                  />
                  <Route
                    path={CLIENT_PROGRESS.route}
                    // @ts-expect-error TS(2739)
                    element={<ClientProgress />}
                  />
                  <Route path={CLIENT_END_OF_SESSION.route} element={<EndOfSession />} />
                  <Route path={CLIENT_ACTIVITIES.route} element={<Assignments />} />
                  <Route path={CLIENT_DOCUMENTS.route} element={<ClientDocuments />} />
                  <Route path={CLIENT_EVALUATION.route} element={<NpTestPlan />} />
                  <Route path={CLIENT_EVALUATION_ADD_MEASURES.route} element={<NpTestPlanMeasureLibrary />} />
                  <Route
                    path={CLIENT_RISK_SUMMARY.route}
                    element={
                      <SafetyPlanPlaceholder
                        title={<FormattedMessage defaultMessage='Risk summary' description='Risk summary page title' />}
                      />
                    }
                  />
                  <Route
                    path={CLIENT_SAFETY_PLAN.route}
                    element={
                      <SafetyPlanPlaceholder
                        title={<FormattedMessage defaultMessage='Safety plan' description='Safety plan page title' />}
                      />
                    }
                  />
                  <Route path={CLIENT_NEW_SAFETY_PLAN.route} element={<CreateSafetyPlan />} />
                  <Route path={CLIENT_NOTES.route} element={<ClientNotesRoutes />}>
                    <Route index element={<ClientNotes />} />
                    <Route path={CLIENT_NOTE.route} element={<ClientNoteDetails />} />
                    <Route path={CLIENT_NEW_NOTE.route} element={<ClientNoteDetails />} />
                  </Route>
                  <Route path={CLIENT_MEDICATIONS.route} element={<Medications />} />
                  <Route path={CLIENT_RECORDS.route} element={<ClientRecords />} />
                </Route>
              </Route>
            </Route>
            <Route path={RECONCILER.route} element={<ReconcilerRoutes />}>
              <Route index element={<ReconcilerDashboard />} />
              <Route path={RECONCILER_DETAILS.route} element={<ReconcilerDetails />} />
            </Route>
            <Route path={ADMIN.route} element={<AdminRoutes />}>
              <Route index element={<AdminHome />} />
              <Route path={ADMIN_NP_PROGRAM.route} element={<NpProgramAdmin />} />
            </Route>
            <Route path={MY_INFO.route} element={<MyInfoRoutes />} />
            <Route path={DOWNLOADS.route} element={<DownloadPaymentHistory />} />
            <Route path={CALENDAR.route} element={<CalendarRoutes />} />
            <Route path={LC_CALENDAR.route}>
              <Route path={LC_CALENDAR_CURRENT.route} element={<CurrentCalendar />} />
              <Route path={LC_CALENDAR_CALLBACK.route} element={<CalendarCallback />} />
              <Route path={LC_CALENDAR_SETUP.route} element={<CalendarSetup />} />
              <Route path={LC_CALENDAR_SETUP_COMPLETE.route} element={<CalendarSetupComplete />} />
              <Route path={LC_CALENDAR_EDIT.route} element={<CalendarSetup isEdit />} />
              <Route path={LC_CALENDAR_EDIT_SELECT_CALENDARS.route} element={<SelectCalendars isEdit />} />
              <Route path={LC_CALENDAR_ACTIVATE.route} element={<CalendarActivationNavBar />}>
                <Route path={LC_CALENDAR_SELECT_CALENDARS.route} element={<SelectCalendars />} />
                <Route path={LC_CALENDAR_ACTIVATE_SETUP.route} element={<CalendarSetup />} />
              </Route>
            </Route>
            <Route path={SCHEDULING.route} element={<SmartScheduling />} />
            <Route path={SCHEDULING_OAUTH_CALLBACK.route} element={<SchedulingOAuth />} />
            <Route path={SCHEDULING_ACTIVATE.route} element={<SchedulingWalkthrough />} />
            <Route path={`${PAYMENT_HISTORY.route}/:type?`} element={<PaymentHistory />} />
            <Route path={CLIENT_SESSION_LOOKUP.route} element={<SessionLookup />} />
            <Route path={ELIGIBILITY_CHECKER.route} element={<EligibilityChecker />} />
            {ActivityRendererRoute && (
              <Route path={CONTENT_BUILDER_ACTIVITY_RENDER.route} element={<ActivityRendererRoute />} />
            )}
            {ContentBuilder && <Route path={CONTENT_BUILDER.route} element={<ContentBuilder />} />}
            <Route path={SETTINGS.route} element={<Settings />} />
            <Route path={MESSENGER.route}>
              <Route index element={<Messenger />} />
              <Route path={MESSENGER_POPOUT.route} element={<PopoutMessenger />} />
            </Route>
            <Route path={DOCUMENTS.route} element={<DocumentsPage />} />
          </Route>
          <Route path='*' element={<NotFoundRoute />} />
        </Route>
      </Route>,
    )
    const router = sentryCreateBrowserRouter(routes, {
      future: {
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_relativeSplatPath: true,
        v7_skipActionErrorRevalidation: true,
      },
    })
    UNSAFE_patchRouterNavigate(router)
    UNSAFE_listenBeforeNavigate(router, preserveSearchParamsListener)
    return router
  }, [])
  return (
    <PersistGate loading={null} persistor={persistor}>
      <LDOverride ldClient={ldClient} disable={!__DEV__}>
        <SafeAreaProvider>
          <Provider store={store}>
            <ApplicationContext>
              <DevMocksProvider>
                <FocusVisibleManager>
                  <ThemeProvider uppercaseEyebrows>
                    <AppContainer>
                      <ErrorBoundaryWrapper>
                        <IntlProvider locale='en-US' defaultLocale='en-US'>
                          <Suspense fallback={<LoadingIndicator />}>
                            <RouterProvider router={router} />
                          </Suspense>
                        </IntlProvider>
                      </ErrorBoundaryWrapper>
                    </AppContainer>
                  </ThemeProvider>
                </FocusVisibleManager>
              </DevMocksProvider>
            </ApplicationContext>
          </Provider>
        </SafeAreaProvider>
      </LDOverride>
    </PersistGate>
  )
}

const CommonComponents: FunctionComponent = () => {
  const { isSideNavigationBarEnabled, isClientHeaderRedesignEnabled, isZendeskWidgetEnabled } = useFlags()
  const config = useSelector(getAuthConfig)
  usePathChanged()
  const location = useLocation()
  const isActivityRenderer =
    __CONTENT_BUILDER_AVAILABLE__ && location.pathname.replace(/\/+$/, '') === CONTENT_BUILDER_ACTIVITY_RENDER.route
  const isRedesignedScheduleSessionRoute =
    isClientHeaderRedesignEnabled &&
    (location.pathname === CLIENTS_NEW_SESSION.route || location.pathname === CLIENTS_NEW_SESSION_DETAILS.route)
  const hideHeader = shouldHideMainNav(location.pathname) || isRedesignedScheduleSessionRoute
  const isPopout = location.pathname.includes(MESSENGER_POPOUT.route)
  const prevIsPopout = usePrevious(isPopout)
  const videoSessionOpen = useSelector(getLTVideoSessionOpen)
  const supervisor = useSelector(getAuthSupervisor)
  const BCProviders = useSelector(getAuthLTProviders)
  const shouldSeeSupervisorSearch = !isEmpty(supervisor) && !isEmpty(BCProviders)

  useEffect(() => {
    if (prevIsPopout && !isPopout) {
      window.close()
    }
  }, [prevIsPopout, isPopout])

  if (isActivityRenderer) return <Outlet />

  return (
    <HostWrapper>
      {!hideHeader && !isPopout && <Header />}
      <ContentContainerRightOfSideNav
        id={PAGE_SCROLL_CONTAINER_ID}
        marginLeftValue={isSideNavigationBarEnabled && config?.bcPlatform && !hideHeader && !isPopout ? '102px' : '0px'}
        isSideNavigationBarEnabled={isSideNavigationBarEnabled}
      >
        <PageLoader />
        {!isPopout && (
          <>
            {config?.videoSessionsEnabled && (
              <StickyProviderSessionContainer
                isSticky={videoSessionOpen || !shouldSeeSupervisorSearch}
                elevateAboveHeader={isClientHeaderRedesignEnabled}
              >
                <ProviderSession />
              </StickyProviderSessionContainer>
            )}
            {config?.bcPlatform && !config?.hideSearchNav ? (
              isSideNavigationBarEnabled ? (
                <GoSearch />
              ) : (
                <GoSearchBar />
              )
            ) : null}
            {isZendeskWidgetEnabled && config?.zendeskWidget && <ZendeskWidget />}
          </>
        )}
        <AlertsContainer />
        <ToastContainer hasBiggerMarginLeftValue={isSideNavigationBarEnabled} />
        <Outlet />
      </ContentContainerRightOfSideNav>
    </HostWrapper>
  )
}

export default withLDProvider({
  clientSideID: LAUNCHDARKLY_WEB_CLIENT_ID,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  context: {
    kind: 'user',
    key: LD_SHARED_CONTEXT_KEYS.LAUNCH_DARKLY_USER_UUID,
  },
  options: {
    sendEvents: !shouldDisableCostlyVendors,
  },
})(App)
